import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthClient, SessionClient, type SessionContext as Session } from "../api/generated";

export type AccessLevel = "NONE" | "READ" | "WRITE" | "ADMIN";

const TOKEN_KEY = "tenfour.access_token";
const REFRESH_KEY = "tenfour.refresh_token";

interface SessionState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const SessionCtx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const sessionClient = useMemo(() => new SessionClient(""), []);
  const authClient = useMemo(() => new AuthClient(""), []);

  const refresh = useCallback(async () => {
    if (!window.localStorage.getItem(TOKEN_KEY)) {
      setSession(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setSession(await sessionClient.session_Context());
      setError(null);
    } catch {
      // 401 means the credential, not the permission. The two are never conflated (§6.6).
      window.localStorage.removeItem(TOKEN_KEY);
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [sessionClient]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      try {
        const tokens = await authClient.auth_Login({ email, password });
        window.localStorage.setItem(TOKEN_KEY, tokens.accessToken!);
        window.localStorage.setItem(REFRESH_KEY, tokens.refreshToken!);
        await refresh();
      } catch {
        setError("Email or password is incorrect.");
        throw new Error("sign-in failed");
      }
    },
    [authClient, refresh],
  );

  const signOut = useCallback(() => {
    window.localStorage.removeItem(TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_KEY);
    setSession(null);
  }, []);

  const value = useMemo<SessionState>(
    () => ({ session, loading, error, signIn, signOut, refresh }),
    [session, loading, error, signIn, signOut, refresh],
  );

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionCtx);
  if (!ctx) throw new Error("useSession must be used inside SessionProvider");
  return ctx;
}

/**
 * The ONLY permission hook. Do not invent additional helpers — the console renders permissions
 * reported by the API and never enforces them; hiding a button is UX, not security (§10).
 */
export function usePermissions() {
  const { session } = useSession();

  const hasCapability = (capability: string): boolean =>
    !!session && Object.values(session.modules ?? {}).some((m) => m.capabilities?.includes(capability));

  const moduleLevel = (module: string): AccessLevel =>
    ((session?.modules?.[module]?.level as AccessLevel) ?? "NONE");

  const canSee = (module: string): boolean => moduleLevel(module) !== "NONE";

  return { hasCapability, moduleLevel, canSee, session };
}
